/**
 * 관리자 전역 상태 — adminToken 보관 및 로그인/로그아웃.
 * 토큰은 localStorage 에 영속화하고, API 클라이언트가 자동 첨부한다.
 */
import { create } from 'zustand';

const TOKEN_KEY = 'kome.adminToken';
const EXPIRES_KEY = 'kome.adminTokenExpiresAt';

function readToken(): string | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const expiresAt = Number(localStorage.getItem(EXPIRES_KEY) ?? 0);
    if (!token) return null;
    // 만료된 토큰은 폐기
    if (expiresAt && Date.now() >= expiresAt) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(EXPIRES_KEY);
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

interface AdminState {
  adminToken: string | null;
  /** 인증 여부 (가드/라우팅에서 사용) */
  isAuthed: boolean;
  /** 로그인 성공 시 토큰 저장 */
  setSession: (adminToken: string, expiresAt: number) => void;
  /** 로그아웃 — 토큰 폐기 */
  logout: () => void;
}

export const useAdminStore = create<AdminState>((set) => {
  const initial = readToken();
  return {
    adminToken: initial,
    isAuthed: !!initial,
    setSession: (adminToken, expiresAt) => {
      try {
        localStorage.setItem(TOKEN_KEY, adminToken);
        localStorage.setItem(EXPIRES_KEY, String(expiresAt));
      } catch {
        // 저장 실패해도 메모리 상태는 유지
      }
      set({ adminToken, isAuthed: true });
    },
    logout: () => {
      try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EXPIRES_KEY);
      } catch {
        // ignore
      }
      set({ adminToken: null, isAuthed: false });
    },
  };
});

/** 스토어 밖(예: API 클라이언트)에서 현재 토큰을 읽기 위한 헬퍼 */
export function getAdminToken(): string | null {
  return useAdminStore.getState().adminToken;
}

/** 401 등으로 강제 로그아웃 시 호출 */
export function clearAdminSession(): void {
  useAdminStore.getState().logout();
}
